/* eslint-disable @typescript-eslint/no-explicit-any */
import { call, put, takeLatest, all } from "redux-saga/effects";
import {
  loginEmail,
  loginGoogle,
  loginSuccess,
  sessionError,
  logout,
  resetSessionAll,
  forgotPassword,
  signupEmail,
  setIsLoading,
} from "../slices/session/sessionSlice";
import { resetStore } from "../actions";
import { usersApi } from "../apis/usersApi";
import { companyApi } from "../apis/companyApi";
import { campaignApi } from "../apis/campaignApi";
import { metricsApi } from "../apis/metricsApi";
import {
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  UserCredential,
  signOut,
  sendPasswordResetEmail,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  User,
} from "firebase/auth";
import { UserApp } from "../slices/session/sessionTypes";
import { AppExceptionType, ErrorCause } from "../../types/ErrorTypes";
import { analytics, auth } from "../../firebase/firebase";
import { navigate } from "../../navigation/navigation";
import { logEvent, setUserId, setUserProperties } from "firebase/analytics";
import { internalCampaignsMergeApi } from "../apis/internalCampaignsMergeApi";
import { internalCampaignsMetricsApi } from "../apis/internalCampaignsMetricsApi";
import { internalCampaignApi } from "../apis/internalCampaignApi";
import { creativeMetricsApi } from "../apis/creativeMetricsApi";
import { publisherMetricsApi } from "../apis/publisherMetricsApi";
import * as Sentry from "@sentry/react";
import { customEntitiesApi } from "../apis/customEntitiesApi";
import { insertionOrderApi } from "../apis/insertionOrderApi";
import { insertionOrderMarginApi } from "../apis/insertionOrderMarginApi";
import { dv360Api } from "../apis/dv360Api";
import { hivestackApi } from "../apis/hivestackApi";
import { amazonApi } from "../apis/amazonApi";

function* loginStats(user: User) {
  // Set user ID and properties in Firebase Analytics
  yield call(setUserId, analytics, user.uid);
  yield call(setUserProperties, analytics, {
    email: user.email,
    creation_time: user.metadata.creationTime,
    last_sign_in: user.metadata.lastSignInTime,
  });

  // Log the login event
  yield call(logEvent, analytics, "login", { method: "email" });
}

// Worker saga for email login
function* loginEmailFunc(action: ReturnType<typeof loginEmail>) {
  try {
    const { email, password } = action.payload;
    const userCredential: UserCredential = yield call(
      signInWithEmailAndPassword,
      auth,
      email,
      password
    );
    const user: User = userCredential.user;

    const userApp: UserApp = {
      email: user.email,
      uid: user.uid,
      creationTime: user.metadata.creationTime,
      lastSignInTime: user.metadata.lastSignInTime,
      token: yield user.getIdToken(),
    };
    yield loginStats(user);
    yield put(loginSuccess(userApp));
  } catch (error: any) {
    Sentry.captureException(error);
    const appError: AppExceptionType = {
      message: error.message,
      cause: ErrorCause.UNKNOWN,
    };
    yield put(sessionError(appError));
  } finally {
    yield put(setIsLoading(false));
  }
}

// Worker saga for Google login
function* loginGoogleFunc() {
  try {
    const provider = new GoogleAuthProvider();
    const result: UserCredential = yield call(signInWithPopup, auth, provider);
    const user = result.user;

    const userApp: UserApp = {
      email: user.email,
      uid: user.uid,
      creationTime: user.metadata.creationTime,
      lastSignInTime: user.metadata.lastSignInTime,
      token: yield user.getIdToken(),
    };
    yield loginStats(user);
    yield put(loginSuccess(userApp));
  } catch (error: any) {
    Sentry.captureException(error);
    const appError: AppExceptionType = {
      message: error.message,
      cause: ErrorCause.UNKNOWN,
    };
    yield put(sessionError(appError));
  } finally {
    yield put(setIsLoading(false));
  }
}

function* signupEmailFunc(action: ReturnType<typeof signupEmail>) {
  try {
    const { email, password } = action.payload;
    const userCredential: UserCredential = yield call(
      createUserWithEmailAndPassword,
      auth,
      email,
      password
    );
    const user = userCredential.user;
    const userApp: UserApp = {
      email: user.email,
      uid: user.uid,
      creationTime: user.metadata.creationTime,
      lastSignInTime: user.metadata.lastSignInTime,
      token: yield user.getIdToken(),
    };
    if (!user.emailVerified) {
      yield sendEmailVerification(user);
      navigate("/login");
      alert("A verification email has been sent");
    } else {
      yield put(loginSuccess(userApp));
    }
  } catch (error: any) {
    Sentry.captureException(error);
    yield put(
      sessionError({
        message: error.message,
        cause: ErrorCause.UNKNOWN,
      })
    );
  } finally {
    yield put(setIsLoading(false));
  }
}

function* forgotPasswordFunc(action: ReturnType<typeof forgotPassword>) {
  try {
    yield call(sendPasswordResetEmail, auth, action.payload);
    alert("Password reset email sent");
    navigate("/login");
  } catch (error: any) {
    Sentry.captureException(error);
    console.error("Error sending password reset email", error.message);
    yield put(
      sessionError({
        message: error.message,
        cause: ErrorCause.UNKNOWN,
      })
    );
  } finally {
    yield put(setIsLoading(false));
  }
}

// Páginas que se abren sin sesión desde un enlace. Al arrancar la app sin usuario el
// listener de auth despacha logout, y eso no debe mandar a /login a quien llega a ellas.
const STAY_ON_LOGOUT_PATHS = ["/forgot-password", "/reset-password"];

function* logoutFunc() {
  try {
    console.log("Logging out...");
    // Clear analytics data
    yield call(setUserId, analytics, null);
    yield call(setUserProperties, analytics, { email: null });

    // Sign out from Firebase auth
    yield call(signOut, auth);

    // First invalidate all API caches to ensure no pending requests use stale credentials
    yield put(usersApi.util.invalidateTags(["Users"]));
    yield put(companyApi.util.invalidateTags(["Companies"]));
    yield put(campaignApi.util.invalidateTags(["Campaigns"]));
    yield put(metricsApi.util.invalidateTags(["Metrics"]));
    yield put(
      dv360Api.util.invalidateTags([
        "DV360Partners",
        "DV360Advertisers",
        "DV360Campaigns",
        "DV360Metrics",
        "DV360InsertionOrders",
      ])
    );

    // Then reset all API states to clear any cached data
    yield put(usersApi.util.resetApiState());
    yield put(companyApi.util.resetApiState());
    yield put(campaignApi.util.resetApiState());
    yield put(metricsApi.util.resetApiState());
    yield put(internalCampaignsMergeApi.util.resetApiState());
    yield put(internalCampaignsMetricsApi.util.resetApiState());
    yield put(internalCampaignApi.util.resetApiState());
    yield put(creativeMetricsApi.util.resetApiState());
    yield put(publisherMetricsApi.util.resetApiState());
    yield put(customEntitiesApi.util.resetApiState());
    yield put(insertionOrderApi.util.resetApiState());
    yield put(insertionOrderMarginApi.util.resetApiState());
    yield put(dv360Api.util.resetApiState());
    yield put(hivestackApi.util.resetApiState());
    yield put(amazonApi.util.resetApiState());

    // Reset session state (this is now redundant but kept for backward compatibility)
    yield put(resetSessionAll());

    // Finally reset the entire Redux store to ensure a clean slate
    yield put(resetStore());

    // Navigate to login page
    if (!STAY_ON_LOGOUT_PATHS.includes(window.location.pathname)) {
      navigate("/login");
    }

    console.log("Logout completed, all data cleared");
  } catch (error: any) {
    Sentry.captureException(error);
    console.error("Error during logout:", error.message);
  } finally {
    yield put(setIsLoading(false));
  }
}

export function* sessionSaga() {
  yield all([
    takeLatest(loginEmail.type, loginEmailFunc),
    takeLatest(loginGoogle.type, loginGoogleFunc),
    takeLatest(signupEmail.type, signupEmailFunc),
    takeLatest(forgotPassword.type, forgotPasswordFunc),
    takeLatest(logout.type, logoutFunc),
  ]);
}
